/**
 * Sync Status Badge Component
 * 
 * Displays sync status with visual indicators and detailed information
 */

import React from 'react';
import { Badge } from '@tracekit/core';
import { CheckCircle, XCircle, AlertCircle, Clock, RefreshCw, TrendingUp } from 'lucide-react';

export interface SyncStatus {
  status: 'success' | 'error' | 'in_progress' | 'pending' | 'never';
  lastSync?: string | null;
  nextSync?: string | null;
  itemCount?: number;
  errorMessage?: string;
  syncDuration?: number; // in milliseconds
}

export interface SyncStatusBadgeProps {
  status: SyncStatus;
  size?: 'sm' | 'md' | 'lg';
  showDetails?: boolean;
}

export function SyncStatusBadge({ status, size = 'md', showDetails = false }: SyncStatusBadgeProps) {
  const getStatusConfig = () => {
    switch (status.status) {
      case 'success':
        return {
          icon: CheckCircle,
          color: 'text-green-600',
          bgColor: 'bg-green-50 dark:bg-green-900/20',
          borderColor: 'border-green-200 dark:border-green-800',
          badgeVariant: 'default' as const,
          label: 'Synced',
        };
      case 'error':
        return {
          icon: XCircle,
          color: 'text-red-600',
          bgColor: 'bg-red-50 dark:bg-red-900/20',
          borderColor: 'border-red-200 dark:border-red-800',
          badgeVariant: 'destructive' as const,
          label: 'Sync Failed',
        };
      case 'in_progress':
        return {
          icon: RefreshCw,
          color: 'text-blue-600',
          bgColor: 'bg-blue-50 dark:bg-blue-900/20',
          borderColor: 'border-blue-200 dark:border-blue-800',
          badgeVariant: 'default' as const,
          label: 'Syncing...',
          animate: true,
        };
      case 'pending':
        return {
          icon: Clock,
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
          borderColor: 'border-yellow-200 dark:border-yellow-800',
          badgeVariant: 'secondary' as const,
          label: 'Pending',
        };
      case 'never':
      default:
        return {
          icon: Clock,
          color: 'text-gray-600',
          bgColor: 'bg-gray-50 dark:bg-gray-900/20',
          borderColor: 'border-gray-200 dark:border-gray-800',
          badgeVariant: 'secondary' as const,
          label: 'Never Synced',
        };
    }
  };

  const config = getStatusConfig();
  const StatusIcon = config.icon;
  const iconSize = size === 'sm' ? 'h-3 w-3' : size === 'lg' ? 'h-5 w-5' : 'h-4 w-4';

  const formatLastSync = (lastSync: string | null | undefined): string => {
    if (!lastSync) return 'Never';
    
    try {
      const date = new Date(lastSync);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString();
    } catch {
      return 'Unknown';
    }
  };

  const formatNextSync = (nextSync: string | null | undefined): string => {
    if (!nextSync) return 'Not scheduled';
    
    try {
      const date = new Date(nextSync);
      const now = new Date();
      const diffMs = date.getTime() - now.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 0) return 'Overdue';
      if (diffMins < 60) return `in ${diffMins}m`;
      if (diffHours < 24) return `in ${diffHours}h`;
      if (diffDays < 7) return `in ${diffDays}d`;
      return date.toLocaleDateString();
    } catch {
      return 'Unknown';
    }
  };

  const formatDuration = (ms: number | undefined): string => {
    if (!ms) return '';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  return (
    <div className={`inline-flex items-center gap-2 ${config.bgColor} ${config.borderColor} border rounded-lg px-2 py-1`}>
      <StatusIcon 
        className={`${iconSize} ${config.color} ${config.animate ? 'animate-spin' : ''}`} 
      />
      <div className="flex flex-col">
        <Badge variant={config.badgeVariant} className="text-xs">
          {config.label}
        </Badge>
        {showDetails && (
          <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
            {status.lastSync && (
              <span>Last: {formatLastSync(status.lastSync)}</span>
            )}
            {status.nextSync && (
              <span className="ml-2">Next: {formatNextSync(status.nextSync)}</span>
            )}
            {status.itemCount !== undefined && (
              <span className="ml-2 flex items-center gap-1">
                <TrendingUp className="h-3 w-3" />
                {status.itemCount}
              </span>
            )}
            {status.syncDuration && (
              <span className="ml-2">({formatDuration(status.syncDuration)})</span>
            )}
          </div>
        )}
      </div>
      {status.errorMessage && showDetails && (
        <div className="text-xs text-red-600 dark:text-red-400 ml-2">
          {status.errorMessage}
        </div>
      )}
    </div>
  );
}


